import { ErrorBoundaryRouted } from '@src/components/ErrorBoundary';
import { ContextProvider } from '@src/providers';
import {
  DROPDOWN_PORTAL_ID,
  MODAL_PORTAL_ID,
  POPOVER_PORTAL_ID,
  PORTAL_PARENT_CLASS,
  PortalContextProvider,
  useAuthContext,
  usePortal,
} from '@talos/kyoko';
import { ROUTER_CREATE_FUTURE_FLAGS, ROUTER_PROVIDER_FUTURE_FLAGS } from '@talos/kyoko/src/routed';
import { useEffect } from 'react';
import { createBrowserRouter, Route, RouterProvider, Routes } from 'react-router-dom';
import { POPOUT_URL } from '../../components/Layout/tokens';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import MobilePages from '../Mobile/MobilePages';
import { Unauthorized } from '../Unauthorized';
import { EmbeddedPages } from './EmbeddedPages';
import { Pages } from './Pages';

function AuthenticatedPages() {
  return (
    <ContextProvider>
      <PortalContextProvider>
        <Pages />
      </PortalContextProvider>
    </ContextProvider>
  );
}

function AuthenticatedMobilePages() {
  return (
    <ContextProvider>
      <MobilePages />
    </ContextProvider>
  );
}

function MobileAppRoutes() {
  const auth = useAuthContext();
  return (
    <Routes>
      {auth.isAuthenticated && <Route path="*" element={<AuthenticatedMobilePages />} />}
      {!auth.isAuthenticated && <Route path="/unauthorized/*" element={<Unauthorized />} />}
    </Routes>
  );
}

function DeviceRouter() {
  const isMobileLayout = useIsMobileLayout();
  return isMobileLayout ? <MobileAppRoutes /> : <AppRoutesInner />;
}

function AppRoutesInner() {
  const auth = useAuthContext();

  const { setPortalRef: modalPortalRef } = usePortal(MODAL_PORTAL_ID);
  const { setPortalRef: dropdownPortalRef } = usePortal(DROPDOWN_PORTAL_ID);
  const { setPortalRef: popoverPortalRef } = usePortal(POPOVER_PORTAL_ID);

  return (
    <>
      {/* Portal targets for rendering our components at the top level */}
      <div
        ref={modalPortalRef}
        id={MODAL_PORTAL_ID}
        data-testid={MODAL_PORTAL_ID}
        className={`ag-custom-component-popup ${PORTAL_PARENT_CLASS}`}
      />
      <div
        ref={dropdownPortalRef}
        id={DROPDOWN_PORTAL_ID}
        data-testid={DROPDOWN_PORTAL_ID}
        className={`ag-custom-component-popup ${PORTAL_PARENT_CLASS}`}
      />
      <div
        ref={popoverPortalRef}
        id={POPOVER_PORTAL_ID}
        data-testid={POPOVER_PORTAL_ID}
        className={`ag-custom-component-popup ${PORTAL_PARENT_CLASS}`}
      />
      <Routes>
        {auth.isAuthenticated && (
          <>
            <Route path={POPOUT_URL} element={<SelfDestructIfEmpty />} />
            <Route path="/embedded/*" element={<EmbeddedPages />} />
            <Route path="*" element={<AuthenticatedPages />} />
          </>
        )}
        {!auth.isAuthenticated && <Route path="/unauthorized/*" element={<Unauthorized />} />}
      </Routes>
    </>
  );
}

const browserRouter = createBrowserRouter(
  [{ path: '*', element: <DeviceRouter />, errorElement: <ErrorBoundaryRouted /> }, {}],
  {
    future: ROUTER_CREATE_FUTURE_FLAGS,
  }
);

export const AppRoutes = () => <RouterProvider future={ROUTER_PROVIDER_FUTURE_FLAGS} router={browserRouter} />;

const DOCKVIEW_POPOUT_CONTENT_SELECTOR = '.dv-popout-window > .dv-groupview > .dv-content-container > .dv-react-part';
// This component is used to close the popout window if it is empty
// This is a workaround for the issue where duplicate popout windows are created
const SelfDestructIfEmpty = () => {
  useEffect(() => {
    setTimeout(() => {
      // Selector for the content container of the popout window
      const contentContainer = document
        .getElementById('dv-popout-window')
        ?.querySelector(DOCKVIEW_POPOUT_CONTENT_SELECTOR);
      // Check if the content container is empty
      // If it is, close the popout window
      if (contentContainer?.childElementCount === 0) {
        window.close();
      }
    }, 100);
  }, []);

  return <></>;
};
